import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type DragEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { DocumentoPatroclo, VisibilidadDocumento } from '@manejo-alcanza/contracts';
import {
  borrarDocumentoPatroclo,
  descargarOriginalPatroclo,
  documentoPatroclo,
  documentosPatroclo,
  ErrorApi,
  subirDocumentoPatroclo,
} from '../api';
import { Aviso, Boton, Card, Cargando, Error_, Tabla } from '../componentes/basicos';
import { NavPatroclo } from '../componentes/patroclo/NavPatroclo';

/**
 * La base de conocimiento de Patroclo: lo que sabe además de los datos.
 * **Solo administrador y desarrollador**, aunque el chat se abra a otros.
 *
 * Portado de `paginas/Documentos.tsx` de Patroclo-Local y ampliado (traspaso
 * §6): además de subir, listar y borrar, se puede **ver** un documento —sus
 * fragmentos en orden, tal como los recupera Patroclo— y **bajar el original**.
 * Es como se revisa qué sabe el agente y cómo quedó partido cada archivo.
 *
 * La visibilidad por omisión es **interna**: lo que se suba sin pensarlo no
 * debe quedar a la vista de un futuro asistente para clientes.
 *
 * El botón dice «Borrar» y no «Desactivar»: aquí borrar es de verdad —Patroclo
 * quita el documento y sus vectores—, y llamarlo de otra forma mentiría.
 */

const EXTENSIONES = ['.pdf', '.txt', '.md', '.docx', '.csv'];
/** El tope del proxy y de Patroclo. */
const MAXIMO_MB = 10;

type EstadoSubida =
  | { tipo: 'inactivo' }
  | { tipo: 'procesando'; nombre: string }
  | { tipo: 'hecho'; mensaje: string }
  | { tipo: 'error'; mensaje: string };

export function PatrocloDocumentos(): ReactNode {
  const cliente = useQueryClient();
  const lista = useQuery({ queryKey: ['patroclo', 'documentos'], queryFn: documentosPatroclo });
  const [visibilidad, setVisibilidad] = useState<VisibilidadDocumento>('interno');
  const [subida, setSubida] = useState<EstadoSubida>({ tipo: 'inactivo' });
  const [arrastrando, setArrastrando] = useState(false);
  const entrada = useRef<HTMLInputElement>(null);

  const refrescar = (): void => void cliente.invalidateQueries({ queryKey: ['patroclo', 'documentos'] });
  const procesando = subida.tipo === 'procesando';

  const subir = async (archivo: File): Promise<void> => {
    // Se valida aquí para no hacer esperar a nadie; la API y Patroclo lo
    // vuelven a validar todo.
    const extension = archivo.name.slice(archivo.name.lastIndexOf('.')).toLowerCase();
    if (!EXTENSIONES.includes(extension)) {
      setSubida({ tipo: 'error', mensaje: `Ese formato no se puede subir. Se aceptan: ${EXTENSIONES.join(', ')}.` });
      return;
    }
    if (archivo.size > MAXIMO_MB * 1024 * 1024) {
      setSubida({ tipo: 'error', mensaje: `El archivo pasa del máximo de ${MAXIMO_MB} MB.` });
      return;
    }
    setSubida({ tipo: 'procesando', nombre: archivo.name });
    try {
      const r = await subirDocumentoPatroclo(archivo, visibilidad);
      setSubida({ tipo: 'hecho', mensaje: r.mensaje });
      refrescar();
    } catch (e) {
      setSubida({ tipo: 'error', mensaje: e instanceof ErrorApi ? e.message : 'No se pudo subir el archivo.' });
    }
  };

  const alSoltar = (ev: DragEvent<HTMLDivElement>): void => {
    ev.preventDefault();
    setArrastrando(false);
    const archivo = ev.dataTransfer.files[0];
    if (archivo && !procesando) void subir(archivo);
  };

  const documentos = lista.data ?? [];
  const totalFragmentos = documentos.reduce((s, d) => s + d.fragmentos, 0);

  return (
    <div className="space-y-4">
      <NavPatroclo />
      <div>
        <h1 className="text-lg font-semibold">Base de conocimiento</h1>
        <p className="text-sm text-tenue">
          Lo que Patroclo sabe además de los datos de ManejoAlcanza: fichas de producto, procesos, la empresa. Solo
          responde con lo que hay aquí.
        </p>
      </div>

      {/* DISENO.md: la caja de soltar archivos, grande y con borde punteado. */}
      <div
        role="button"
        tabIndex={0}
        aria-disabled={procesando}
        onClick={() => !procesando && entrada.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && !procesando && entrada.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={alSoltar}
        className={`flex min-h-32 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed p-6 text-center ${
          arrastrando ? 'border-acento bg-acento-tenue' : 'border-borde bg-superficie'
        } ${procesando ? 'cursor-wait opacity-70' : ''}`}
      >
        {procesando ? (
          <>
            <strong>Procesando «{subida.nombre}»…</strong>
            <span className="text-xs text-tenue">Extrayendo el texto, partiéndolo en fragmentos e indexándolo.</span>
          </>
        ) : (
          <>
            <strong>Arrastra un archivo aquí o tócalo para elegirlo</strong>
            <span className="text-xs text-tenue">
              {EXTENSIONES.join(' · ')} — máximo {MAXIMO_MB} MB
            </span>
          </>
        )}
        <input
          ref={entrada}
          type="file"
          accept={EXTENSIONES.join(',')}
          hidden
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            if (archivo) void subir(archivo);
            // Para poder volver a elegir el mismo archivo.
            e.target.value = '';
          }}
        />
      </div>

      <fieldset className="flex flex-col gap-2 text-sm sm:flex-row sm:gap-6" disabled={procesando}>
        <legend className="mb-1 text-xs font-medium text-tenue">Quién lo puede usar</legend>
        <label className="flex min-h-[44px] items-center gap-2 sm:min-h-0">
          <input
            type="radio"
            name="visibilidad"
            checked={visibilidad === 'interno'}
            onChange={() => setVisibilidad('interno')}
          />
          Interno: solo el equipo de Alcanza
        </label>
        <label className="flex min-h-[44px] items-center gap-2 sm:min-h-0">
          <input
            type="radio"
            name="visibilidad"
            checked={visibilidad === 'publico'}
            onChange={() => setVisibilidad('publico')}
          />
          Público: también un futuro asistente para clientes
        </label>
      </fieldset>

      {subida.tipo === 'hecho' && <Aviso>{subida.mensaje}</Aviso>}
      {subida.tipo === 'error' && (
        <div role="alert">
          <Error_>{subida.mensaje}</Error_>
        </div>
      )}

      {lista.isLoading && <Cargando que="los documentos" />}
      {lista.isError && <Error_>{lista.error instanceof ErrorApi ? lista.error.message : 'No se pudieron cargar los documentos.'}</Error_>}
      {lista.data && (
        <>
          <p className="text-xs text-tenue">
            {documentos.length} documentos · {totalFragmentos} fragmentos indexados
          </p>
          <Tabla>
            <thead>
              <tr className="border-b border-borde text-left text-xs uppercase tracking-wide text-tenue">
                <th className="px-3 py-2 font-medium">Documento</th>
                <th className="px-3 py-2 font-medium">Tipo</th>
                <th className="px-3 py-2 text-right font-medium">Fragmentos</th>
                <th className="px-3 py-2 font-medium">Visibilidad</th>
                <th className="px-3 py-2 font-medium">Subido por</th>
              </tr>
            </thead>
            <tbody>
              {documentos.map((d) => (
                <tr key={d.id} className="border-b border-borde last:border-0 hover:bg-fondo">
                  {/* La fila entera lleva al detalle (DISENO.md, «Tabla»). */}
                  <td className="px-3 py-2">
                    <Link to={`/patroclo/documentos/${d.id}`} className="font-medium text-acento hover:underline">
                      {d.titulo}
                    </Link>
                    <div className="text-xs text-tenue">
                      {d.nombre_original} · {tamano(d.tamano_bytes)}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs uppercase">{d.tipo}</td>
                  <td className="cifra px-3 py-2">{d.fragmentos}</td>
                  <td className="px-3 py-2">
                    <PillVisibilidad visibilidad={d.visibilidad} />
                  </td>
                  <td className="px-3 py-2 text-sm">{d.subido_por}</td>
                </tr>
              ))}
              {documentos.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-tenue">
                    Todavía no hay documentos.
                  </td>
                </tr>
              )}
            </tbody>
          </Tabla>
        </>
      )}
    </div>
  );
}

/** Un documento: sus datos, el original y sus fragmentos en orden. */
export function PatrocloDocumento(): ReactNode {
  const { id = '' } = useParams();
  const navegar = useNavigate();
  const cliente = useQueryClient();
  const doc = useQuery({ queryKey: ['patroclo', 'documento', id], queryFn: () => documentoPatroclo(id) });
  const [error, setError] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);

  if (doc.isLoading) return <Cargando que="el documento" />;
  if (doc.isError || !doc.data) {
    return (
      <div className="space-y-4">
        <NavPatroclo />
        <Error_>{doc.error instanceof ErrorApi ? doc.error.message : 'No se pudo abrir el documento.'}</Error_>
      </div>
    );
  }

  const { documento: d, fragmentos } = doc.data;

  const bajar = async (): Promise<void> => {
    setBajando(true);
    setError(null);
    try {
      await descargarOriginalPatroclo(d.id, d.nombre_original);
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo bajar el original.');
    } finally {
      setBajando(false);
    }
  };

  const borrar = async (): Promise<void> => {
    if (!window.confirm(`¿Borrar «${d.titulo}» y sus ${d.fragmentos} fragmentos? Patroclo dejará de saberlo, y no se puede deshacer.`)) {
      return;
    }
    try {
      await borrarDocumentoPatroclo(d.id);
      void cliente.invalidateQueries({ queryKey: ['patroclo', 'documentos'] });
      void navegar('/patroclo/documentos');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo borrar el documento.');
    }
  };

  return (
    <div className="space-y-4">
      <NavPatroclo />
      <div>
        <Link to="/patroclo/documentos" className="text-xs text-tenue underline">
          ← Base de conocimiento
        </Link>
        <h1 className="mt-1 text-lg font-semibold">{d.titulo}</h1>
      </div>

      <Card>
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
          <Dato etiqueta="Archivo" valor={`${d.nombre_original} · ${tamano(d.tamano_bytes)}`} />
          <Dato etiqueta="Visibilidad" valor={<PillVisibilidad visibilidad={d.visibilidad} />} />
          <Dato etiqueta="Fragmentos" valor={String(d.fragmentos)} />
          <Dato etiqueta="Subido por" valor={d.subido_por} />
          <Dato etiqueta="Subido el" valor={d.subido_en ? d.subido_en.slice(0, 10) : 'no se sabe'} />
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          <Boton tipo="secundario" onClick={() => void bajar()} deshabilitado={bajando}>
            {bajando ? 'Bajando…' : 'Bajar el original'}
          </Boton>
          {d.puede_borrar && (
            <Boton tipo="peligro" onClick={() => void borrar()}>
              Borrar
            </Boton>
          )}
        </div>
        {!d.puede_borrar && (
          <p className="mt-2 text-xs text-tenue">
            {d.subido_por === 'Base inicial'
              ? 'Es de la base inicial: no se borra desde aquí.'
              : 'Solo quien lo subió lo puede borrar.'}
          </p>
        )}
        {error && (
          <div className="mt-3" role="alert">
            <Error_>{error}</Error_>
          </div>
        )}
      </Card>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">Fragmentos, en orden</h2>
        <p className="text-xs text-tenue">
          Así quedó partido el documento, y así lo recupera Patroclo: cada fragmento empieza con la línea de contexto
          que ve el modelo.
        </p>
        {fragmentos.map((f) => (
          <article key={f.numero} className="rounded-md border border-borde bg-superficie p-3">
            <div className="mb-1 flex flex-wrap items-baseline gap-x-2 text-xs text-tenue">
              <span className="font-semibold text-texto">#{f.numero}</span>
              {f.seccion && <span>Sección: {f.seccion}</span>}
              {f.pagina !== null && <span>Página {f.pagina}</span>}
            </div>
            <p className="whitespace-pre-wrap text-sm">{f.texto}</p>
          </article>
        ))}
        {fragmentos.length === 0 && <p className="text-tenue">Este documento no tiene fragmentos.</p>}
      </section>
    </div>
  );
}

function Dato({ etiqueta, valor }: { etiqueta: string; valor: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs font-medium text-tenue">{etiqueta}</dt>
      <dd className="break-words">{valor}</dd>
    </div>
  );
}

function PillVisibilidad({ visibilidad }: { visibilidad: DocumentoPatroclo['visibilidad'] }): ReactNode {
  return visibilidad === 'interno' ? (
    <span className="rounded-full bg-neutro-tenue px-2 py-0.5 text-[0.7rem] font-semibold uppercase text-neutro">
      interno
    </span>
  ) : (
    <span className="rounded-full bg-acento-tenue px-2 py-0.5 text-[0.7rem] font-semibold uppercase text-acento">
      público
    </span>
  );
}

function tamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('es-CO', { maximumFractionDigits: 1 })} MB`;
}
